import React, { useEffect, useState } from 'react';
import '../styles/DashboardEditor.css';
import useStore from '../store/useStore';
import { authAPI, companiesAPI } from '../services/api';
import '../styles/DashboardSettings.css';
import AssistantSettings from './assistant/AssistantSettings';

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const ZONES = ['Europe/London', 'Europe/Dublin', 'Europe/Paris', 'Europe/Berlin', 'UTC', 'America/New_York', 'America/Chicago', 'America/Los_Angeles', 'Australia/Sydney'];

/**
 * Settings: personal preferences, and for admins the company-wide settings.
 * Dashboards are built and edited on the dashboard itself; live metric
 * definitions live on the Data sources page.
 */
const SettingsPage = () => {
  const user = useStore((state) => state.user);
  const currentCompany = useStore((state) => state.currentCompany);
  const animationsEnabled = useStore((state) => state.animationsEnabled);
  const setAnimationsEnabled = useStore((state) => state.setAnimationsEnabled);
  const setTheme = useStore((state) => state.setTheme);
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  const theme = user?.theme === 'dark' ? 'dark' : 'light';

  const [company, setCompany] = useState(null);
  const [saving, setSaving] = useState(null);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isAdmin || !currentCompany?.id) { setCompany(null); return; }
    companiesAPI.getById(currentCompany.id)
      .then((res) => setCompany(res?.success ? res.data : null))
      .catch(() => setCompany(null));
  }, [isAdmin, currentCompany?.id]);

  /** Applies straight away, and goes back if it cannot be saved to the user's account. */
  const chooseTheme = async (next) => {
    const previous = theme;
    setTheme(next);
    setError('');
    try {
      const res = await authAPI.updatePreferences({ theme: next });
      if (!res?.success) throw new Error(res?.error);
    } catch (err) {
      setTheme(previous);
      setError(err.response?.data?.error || 'Could not save your theme');
    }
  };

  const save = async (field, value, label) => {
    const previous = company;
    setCompany((c) => ({ ...c, ...{ [snake(field)]: value } }));
    setSaving(field);
    setError('');
    try {
      const res = await companiesAPI.updateSettings(currentCompany.id, { [field]: value });
      if (res?.success) {
        setCompany((c) => ({ ...c, ...res.data }));
        // Money everywhere reads the currency from the current company.
        if (field === 'currency') useStore.setState({ currentCompany: { ...currentCompany, currency: value } });
        setMessage(`${label} saved`);
        setTimeout(() => setMessage(''), 2500);
      }
    } catch (err) {
      setCompany(previous);
      setError(err.response?.data?.error || `Could not save ${label.toLowerCase()}`);
    } finally {
      setSaving(null);
    }
  };

  return (
    <div className="settings-container settings-page">
      <div className="page-header">
        <div>
          <h2>Settings</h2>
          <p className="page-subtitle">To build or change a dashboard, open it and choose <strong>Edit dashboard</strong>.</p>
        </div>
      </div>

      {error && <div className="error-message" role="alert">{error}</div>}
      {message && <div className="success-message" role="status">{message}</div>}

      <section className="settings-section" aria-labelledby="personal-heading">
        <h3 id="personal-heading">Personal preferences</h3>
        <p className="settings-help">These apply only to you.</p>
        <fieldset className="settings-theme">
          <legend>Theme</legend>
          {[['light', 'Light'], ['dark', 'Dark']].map(([value, label]) => (
            <label key={value} className={`settings-theme-choice ${theme === value ? 'selected' : ''}`}>
              <input type="radio" name="theme" value={value} checked={theme === value} onChange={() => chooseTheme(value)} />
              <span className={`settings-theme-preview settings-theme-preview--${value}`} aria-hidden="true"><i /><i /><i /></span>
              <strong>{label}</strong>
            </label>
          ))}
          <small>Saved to your account, so it follows you to any browser.</small>
        </fieldset>
        <label className="settings-toggle">
          <input type="checkbox" checked={animationsEnabled} onChange={(e) => setAnimationsEnabled(e.target.checked)} />
          <span>
            <strong>Animate changing values</strong>
            <small>Live numbers slide in when they update. Set for this browser only.</small>
          </span>
        </label>
      </section>

      {isAdmin && company && (
        <section className="settings-section" aria-labelledby="company-heading">
          <h3 id="company-heading">{currentCompany?.name} settings</h3>
          <p className="settings-help">These apply to everyone in the company.</p>
          <div className="company-settings-grid">
            <label>
              <span>Financial year starts in</span>
              <select value={company.fiscal_year_start_month || 1} disabled={saving !== null} onChange={(e) => save('fiscalYearStartMonth', Number(e.target.value), 'Financial year start')}>
                {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
              </select>
              <small>Used by financial year periods, statements, and quarters and years on KPIs and reports.</small>
            </label>
            <label>
              <span>Currency</span>
              <select value={company.currency || 'GBP'} disabled={saving !== null} onChange={(e) => save('currency', e.target.value, 'Currency')}>
                {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <small>Used wherever money is shown. Amounts are not converted.</small>
            </label>
            <label>
              <span>Timezone</span>
              <input type="text" list="tz-options" defaultValue={company.timezone || 'Europe/London'} disabled={saving !== null}
                onBlur={(e) => e.target.value && e.target.value !== company.timezone && save('timezone', e.target.value, 'Timezone')} />
              <datalist id="tz-options">{ZONES.map((z) => <option key={z} value={z} />)}</datalist>
            </label>
            <label>
              <span>Mark live data stale after (minutes)</span>
              <input type="number" min={1} max={1440} defaultValue={company.default_stale_minutes ?? 5} disabled={saving !== null}
                onBlur={(e) => Number(e.target.value) !== company.default_stale_minutes && save('defaultStaleMinutes', Number(e.target.value), 'Stale threshold')} />
              <small>Each dashboard can override this.</small>
            </label>
            <label>
              <span>Keep live metric history for (days)</span>
              <input type="number" min={1} max={3650} defaultValue={company.metrics_retention_days ?? 30} disabled={saving !== null}
                onBlur={(e) => Number(e.target.value) !== company.metrics_retention_days && save('metricsRetentionDays', Number(e.target.value), 'Retention')} />
            </label>
          </div>
          <label className="settings-toggle">
            <input type="checkbox" checked={!!company.auto_create_metric_definitions} disabled={saving !== null}
              onChange={(e) => save('autoCreateMetricDefinitions', e.target.checked, 'Auto-create setting')} />
            <span>
              <strong>Accept unknown live metrics</strong>
              <small>A push with a new metric key creates its definition automatically.</small>
            </span>
          </label>
        </section>
      )}
      {isAdmin && company && <AssistantSettings companyId={currentCompany.id} companyName={currentCompany.name} />}
    </div>
  );
};

const CURRENCIES = ['GBP', 'EUR', 'USD', 'AUD', 'CAD', 'NZD', 'ZAR', 'CHF', 'SEK', 'NOK', 'DKK'];

const snake = (field) => ({
  currency: 'currency',
  fiscalYearStartMonth: 'fiscal_year_start_month',
  timezone: 'timezone',
  defaultStaleMinutes: 'default_stale_minutes',
  metricsRetentionDays: 'metrics_retention_days',
  autoCreateMetricDefinitions: 'auto_create_metric_definitions',
}[field] || field);

export default SettingsPage;
